import {AttrsSchema, Component} from 'panel';

import {emitTrackEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {RetentionTrackingEvents as Events} from 'common/report/retention/util/constants';
import {DisplaySize} from 'common/components/tooltip/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {RetentionQuerySettingsUpdate} from 'common/report/retention/query-builder/retention-query-settings';
import {RetentionAlignmentType} from 'common/report/retention/util/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {ChangeEvent} from 'common/components/select/types';
import {Bucket} from 'common/widgets/bucket-sizes';
import {pluralize} from 'common/util/string';
import {useRetentionConsistency} from 'common/report/util/feature-sampling';
import {dataGroupStore} from 'common/report/stores/data-groups';

import {template} from './template';
import {CUSTOM_RETENTION_UNIT} from './constants';

const DEFAULT_COLLAPSIBLE_OPEN = true;
interface RetentionQuerySettingsState {
  customRetentionUnitSelected: boolean;
  isBlockOpen: boolean;
  hovered: boolean;
}

interface RetentionQueryAttrs {
  unbounded: boolean;
  'data-group-id': string;
  'bucket-sizes': number[];
  'retention-unit': TimeUnit;
  'retention-alignment-type': RetentionAlignmentType;
}

interface RetentionCriteriaJsxProps {
  attrs?: RetentionQueryAttrs;
}

const commonTooltipAttrs = {
  size: DisplaySize.Small,
};

export type RetentionUnitUpdate = CustomEvent<{
  retentionUnit: TimeUnit;
}>;

export type RetentionBucketSizeUpdate = CustomEvent<{
  retentionUnit: TimeUnit;
  bucketSizes: number[];
}>;

export type RetentionAlignmentUpdate = CustomEvent<{
  retentionAlignmentType: RetentionAlignmentType;
}>;

export class RetentionCriteria extends Component<
  RetentionQuerySettingsState,
  RetentionQueryAttrs,
  EmptyObj,
  EmptyObj,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<RetentionQueryAttrs> {
    return {
      ...super.attrsSchema,
      unbounded: `boolean`,
      'data-group-id': `string`,
      'bucket-sizes': {
        type: `json`,
        default: [],
      },
      'retention-unit': {
        type: `string`,
        default: TimeUnit.Day,
        enum: [TimeUnit.Day, TimeUnit.Month, TimeUnit.Week],
      },
      'retention-alignment-type': {
        type: `string`,
        default: RetentionAlignmentType.Birth,
        enum: Object.values(RetentionAlignmentType),
      },
    };
  }

  get config() {
    return {
      contexts: [`currentProject` as const],

      defaultState: {
        customRetentionUnitSelected: false,
        isBlockOpen: DEFAULT_COLLAPSIBLE_OPEN,
        hovered: false,
      },

      template,
    };
  }

  getRetentionAlignmentModifier(alignmentType: RetentionAlignmentType) {
    return alignmentType === RetentionAlignmentType.Birth ? `` : `Calendar `;
  }

  getLabel(bucketSizes: number[], bucketUnit: TimeUnit, alignmentType: RetentionAlignmentType) {
    const useNewLabel = useRetentionConsistency();
    const alignmentModifier = this.getRetentionAlignmentModifier(alignmentType);
    const boundedModifier = this.attr(`unbounded`) ? `or after Each ` : ` Each `;
    const map = {
      [TimeUnit.Day]: useNewLabel
        ? `Returning on ${boundedModifier}${alignmentModifier}Day`
        : `Each ${alignmentModifier}Day`,
      [TimeUnit.Week]: useNewLabel
        ? `Returning on ${boundedModifier}${alignmentModifier}Week`
        : `Each ${alignmentModifier}Week`,
      [TimeUnit.Month]: useNewLabel
        ? `Returning on ${boundedModifier}${alignmentModifier}Month`
        : `Each ${alignmentModifier}Month`,
    };
    const label = this.getBucketLabel(bucketSizes, true);
    const unit = map[bucketUnit];
    return `${unit} ${label}`;
  }

  getBucketLabel(bucketSizes: number[], truncate = false) {
    let previousBucket = 0;
    const buckets = truncate ? bucketSizes.slice(0, 3) : bucketSizes;
    let label = buckets
      .map((bucket) => {
        const currentBucket = previousBucket + bucket;
        const retLabel = bucket === 1 ? `${currentBucket - 1}` : `${previousBucket}-${currentBucket - 1}`;
        previousBucket = currentBucket;
        return retLabel;
      })
      .join(` / `);
    if (truncate && bucketSizes.length > 3) {
      label += `...`;
    }
    return label;
  }

  getUnitSelectorTooltip = () =>
    this.attr(`bucket-sizes`) && this.attr(`bucket-sizes`).length
      ? {
          text: `Returning users on ${this.attr(`retention-unit`)}s: ${this.getBucketLabel(this.attr(`bucket-sizes`))}`,
          ...commonTooltipAttrs,
        }
      : commonTooltipAttrs;

  getRetentionUnitTooltip = (retentionUnit: TimeUnit) => {
    const dataGroupName = pluralize(dataGroupStore.value?.[this.attr(`data-group-id`)]?.displayName || `Users`, true);
    const alignmentModifier = this.getRetentionAlignmentModifier(this.attr(`retention-alignment-type`));
    const map = {
      [TimeUnit.Day]: `Day`,
      [TimeUnit.Week]: `Week`,
      [TimeUnit.Month]: `Month`,
    };
    return `${dataGroupName} Who Return on ${this.attr(`unbounded`) ? `or after` : ``} each ${alignmentModifier}${
      map[retentionUnit]
    }`;
  };

  handleChangeUnitSelector = (ev) => {
    if (ev.detail.item.value === CUSTOM_RETENTION_UNIT) {
      this.update({customRetentionUnitSelected: true});
    } else {
      const retentionUnit: TimeUnit = ev.detail.item.value;
      const changeUnitEvent: RetentionUnitUpdate = new CustomEvent(`changeUnit`, {
        detail: {retentionUnit},
      });
      this.dispatchEvent(changeUnitEvent);
    }
  };

  handleChangeCountingMethodSelector = (ev: CustomEvent) => {
    const changeSettingEvent: RetentionQuerySettingsUpdate = new CustomEvent(`changeSetting`, {
      detail: {unbounded: ev.detail.item.value === `unbounded`},
    });
    this.dispatchEvent(changeSettingEvent);
  };

  handleCollapsibleQuerySectionChange = (ev: CustomEvent) => {
    const isBlockOpen = ev.detail.state === `open`;
    this.update({isBlockOpen});

    emitTrackEvent(this, {
      eventName: Events.ClickedRetentionCriteria,
      eventPrefix: `mp-query-builder`,
      props: {
        report: `retention`,
        Action: isBlockOpen ? `expand` : `collapse`,
      },
    });
  };

  handleToggleRetentionAlignment = (ev: CustomEvent) => {
    const {
      detail: {on},
    } = ev;
    this.dispatchEvent(
      new CustomEvent(`toggle-retention-alignment`, {
        detail: {retentionAlignmentType: on ? `interval_start` : `birth`},
      }),
    );
  };

  handleApplyCustom = (ev: CustomEvent<{bucketUnit: TimeUnit; buckets: Bucket[]}>) => {
    this.update({customRetentionUnitSelected: false});
    const changeBucketEvent: RetentionBucketSizeUpdate = new CustomEvent(`applyBucketSizes`, {
      detail: {
        retentionUnit: ev.detail.bucketUnit,
        bucketSizes: ev.detail.buckets.map((bucket) => bucket.interval),
      },
    });
    this.dispatchEvent(changeBucketEvent);
    emitTrackEvent(this, {
      eventName: Events.AddCustomRetentionBucket,
      eventPrefix: `mp-query-builder`,
      props: {
        report: `retention`,
      },
    });
  };

  handleCancelCustom = () => {
    this.update({customRetentionUnitSelected: false});
  };

  handleAdvancedMenuChange = (ev: ChangeEvent) => {
    if ([`bounded`, `unbounded`].includes(ev.detail.item.value)) {
      const changeSettingEvent: RetentionQuerySettingsUpdate = new CustomEvent(`changeSetting`, {
        detail: {unbounded: ev.detail.item.value === `unbounded`},
      });
      this.dispatchEvent(changeSettingEvent);
    }
    if (Object.values(RetentionAlignmentType).includes(ev.detail.item.value)) {
      this.dispatchEvent(
        new CustomEvent(`toggle-retention-alignment`, {
          detail: {retentionAlignmentType: ev.detail.item.value},
        }),
      );
    }
  };

  handleMouseEnterUnitSelector = () => {
    this.update({
      hovered: true,
    });
  };

  handleMouseLeaveUnitSelector = () => {
    this.update({
      hovered: false,
    });
  };
}

export const RetentionCriteriaEl = defineAndMakeJsxFactory<RetentionCriteriaJsxProps>(
  `retention-criteria`,
  RetentionCriteria,
);
export const RetentionCriteriaReact = panel2react<RetentionCriteriaJsxProps>(`retention-criteria`, RetentionCriteria);
